import { sanitizeFilename } from "~/utils/tools";
import { convertSubtitleContentFormat, type SubtitleFormat } from "~/utils/subtitle-format";
import type { JobSnapshot, SubtitleTaskDisplayStatus, SubtitleTaskItem } from "~/types/media-subtitle";

export const MAX_SOURCE_FILE_SIZE_BYTES = 3 * 1024 * 1024 * 1024; // 3GB
export const SUPPORTED_MEDIA_TYPES_LABEL =
  "MP3, WAV, M4A, OGG, AAC, FLAC, OPUS, WEBM, MP4, MOV, AVI, MKV, M4V, WMV";

export function buildMaxSourceFileSizeLabel(maxBytes = MAX_SOURCE_FILE_SIZE_BYTES) {
  return `${Math.round(maxBytes / (1024 * 1024))} MB`;
}

export function formatBytes(size: number) {
  if (!size) {
    return "0 B";
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  if (size < 1024 * 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  }

  return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function getReadableFileSize(size: number) {
  if (!size) {
    return "";
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

export function buildDerivedOutputTitle(options: {
  snapshotTitle?: string | null;
  sourceFileName?: string | null;
  fallback?: string;
}) {
  const snapshotTitle = `${options.snapshotTitle || ""}`.trim();
  if (snapshotTitle) {
    return snapshotTitle;
  }

  const sourceName = `${options.sourceFileName || ""}`.trim();
  if (sourceName) {
    return sanitizeFilename(sourceName.replace(/\.[^.]+$/, "")) || (options.fallback || "subtitle");
  }

  return options.fallback || "subtitle";
}

/** Credits per rounded-up minute of source media (video/audio length). */
export { SUBTITLE_CREDITS_PER_MINUTE } from "~/constants/credits";
import { SUBTITLE_CREDITS_PER_MINUTE } from "~/constants/credits";

export function estimateCreditCost(durationSeconds: number) {
  if (!durationSeconds) {
    return 0;
  }

  const minutes = Math.ceil(durationSeconds / 60);
  return minutes * SUBTITLE_CREDITS_PER_MINUTE;
}

export function getAvailableCredits(monthlyLimit: number | string | undefined, bonusLimit: number | string | undefined) {
  return Number(monthlyLimit || 0) + Number(bonusLimit || 0);
}

export function getEffectiveDurationSeconds(jobDuration: number | null | undefined, localDuration: number | undefined) {
  return jobDuration || localDuration || 0;
}

// Empirical baseline: a 197 min source needed ~5 min for convert and ~5 min
// for transcript. That works out to roughly 1 unit of work per ~34 units of
// source. We add a small overhead floor so short clips don't snap to 100%
// before the job actually finishes, and a hard cap so a single estimate stays
// believable for very long inputs.
const PROGRESS_RATIO_DENOMINATOR = 34;
const PROGRESS_MAX_SECONDS = 20 * 60;

export function getEstimatedTranscriptDurationMs(mediaSeconds: number) {
  const ratioSeconds = (mediaSeconds || 0) / PROGRESS_RATIO_DENOMINATOR;
  const estimatedSeconds = Math.min(
    PROGRESS_MAX_SECONDS,
    Math.max(60, Math.round(ratioSeconds) || 60)
  );
  return estimatedSeconds * 1000;
}

export function getEstimatedConvertDurationMs(options: {
  sourceSizeBytes: number;
  mediaSeconds: number;
  isVideoSource: boolean;
}) {
  const sourceSizeMb = options.sourceSizeBytes > 0 ? options.sourceSizeBytes / (1024 * 1024) : 0;
  const durationEstimateSeconds = options.mediaSeconds > 0 ? options.mediaSeconds / PROGRESS_RATIO_DENOMINATOR : 0;
  // Only fall back to size for videos when the duration is missing; raw audio
  // re-encode roughly tracks the source duration, not the byte size.
  const sizeEstimateSeconds = options.isVideoSource && sourceSizeMb > 0 ? sourceSizeMb / 3 : 0;
  const estimatedSeconds = Math.min(
    PROGRESS_MAX_SECONDS,
    Math.max(20, Math.round(Math.max(durationEstimateSeconds, sizeEstimateSeconds)) || 20)
  );

  return estimatedSeconds * 1000;
}

export function getCurrentPreviewSourceFormat(format: SubtitleFormat | null | undefined): SubtitleFormat {
  return format === "vtt" || format === "txt" ? format : "srt";
}

export function getConvertedPreview(preview: string, sourceFormat: SubtitleFormat, targetFormat: SubtitleFormat) {
  if (!preview) {
    return "";
  }

  return convertSubtitleContentFormat(preview, sourceFormat, targetFormat);
}

export function getActiveSourceFileName(selectedFileName?: string, sourceFileName?: string | null) {
  return selectedFileName || sourceFileName || "";
}

export function canDownloadFromJob(job: JobSnapshot | null) {
  return Boolean(job?.jobId && (job.fileAvailability.translated || job.fileAvailability.transcript));
}

export function isWorkflowRunningState(params: {
  isUploading: boolean;
  isConverting: boolean;
  isRunningTranscript: boolean;
  isTranslating: boolean;
  job: JobSnapshot | null;
}) {
  return Boolean(
    params.isUploading ||
      params.isConverting ||
      params.isRunningTranscript ||
      params.isTranslating ||
      params.job?.convertStatus === "processing" ||
      params.job?.transcriptStatus === "processing" ||
      params.job?.translateStatus === "processing"
  );
}

export function canUploadMedia(params: {
  isPro: boolean;
  hasSelectedFile: boolean;
  hasExistingJob: boolean;
  isUploading: boolean;
  isLoadingJob: boolean;
  estimatedCreditCost: number;
  availableCredits: number;
}) {
  return Boolean(
    params.isPro &&
      params.hasSelectedFile &&
      !params.hasExistingJob &&
      !params.isUploading &&
      !params.isLoadingJob &&
      (params.estimatedCreditCost === 0 || params.availableCredits >= params.estimatedCreditCost)
  );
}

export function getBadgeClass(status: string) {
  if (status === "done") {
    return "bg-emerald-500/10 text-emerald-700";
  }
  if (status === "processing") {
    return "bg-primary/10 text-primary";
  }
  if (status === "error") {
    return "bg-destructive/10 text-destructive";
  }
  if (status === "skipped") {
    return "bg-amber-500/10 text-amber-700";
  }
  return "bg-muted text-muted-foreground";
}

/**
 * Collapse a raw platform/JobSnapshot/local status string into one of the four
 * display buckets used by the task list. Accepts the granular `platform_status`
 * (created/uploading/queued/converting/…/ready/failed/canceled), the
 * `JobSnapshot.status` values, or - as a fallback - the local mapping status
 * (reserved/submitted/processing/completed/released/failed).
 */
export function deriveTaskDisplayStatus(
  status: string | null | undefined,
  localStatus?: string | null
): SubtitleTaskDisplayStatus {
  const value = `${status || ""}`.trim().toLowerCase();

  if (value === "ready" || value === "completed") {
    return "ready";
  }
  if (value === "failed" || value === "canceled" || value === "cancelled" || value === "released" || value === "error") {
    return "failed";
  }
  if (value === "created" || value === "uploading" || value === "uploaded" || value === "reserved") {
    return "uploading";
  }
  if (value) {
    // queued / converting / converted / transcribing / transcript_ready /
    // translating / submitted / processing
    return "processing";
  }

  // No platform status yet - fall back to the local mapping status.
  const local = `${localStatus || ""}`.trim().toLowerCase();
  if (local === "completed") return "ready";
  if (local === "failed" || local === "released") return "failed";
  if (local === "reserved") return "uploading";
  if (local) return "processing";

  return "processing";
}

/**
 * Whether a task is still moving (used to decide if it should be polled). A
 * task past the retention window is never active: no job runs that long, so
 * an in-flight status that old is a dead job, not one worth polling.
 */
export function isTaskActive(item: Pick<SubtitleTaskItem, "platformStatus" | "localStatus" | "createdAt">) {
  if (isTaskExpired(item.createdAt)) {
    return false;
  }
  const display = deriveTaskDisplayStatus(item.platformStatus, item.localStatus);
  return display === "uploading" || display === "processing";
}

/**
 * Coarse progress estimate (0–100) for a task row, derived from its status.
 * Returns null while uploading (the row shows a spinner instead of a fake bar).
 */
export function deriveTaskProgress(
  status: string | null | undefined,
  translateProgress?: SubtitleTaskItem["translateProgress"]
): number | null {
  const value = `${status || ""}`.trim().toLowerCase();
  switch (value) {
    case "ready":
    case "completed":
      return 100;
    case "translating":
      // Chunked translation reports real progress; keep a visible floor so a
      // freshly started job doesn't show an empty bar.
      if (translateProgress && translateProgress.total > 0) {
        return Math.min(99, Math.max(5, Math.round(translateProgress.percentage)));
      }
      return 85;
    case "transcript_ready":
      return 70;
    case "transcribing":
    case "converted":
      return 55;
    case "converting":
      return 30;
    case "queued":
    case "submitted":
    case "processing":
      return 10;
    default:
      return null;
  }
}

/**
 * Progress (0–100) of an AI translate task from its chunk counter. Before the
 * first chunk reports, the counter has no total yet - show a small floor
 * instead of the media pipeline's "translating = 85%" estimate.
 */
export function deriveTranslateTaskPercent(
  translateProgress: SubtitleTaskItem["translateProgress"] | undefined
): number {
  if (translateProgress && translateProgress.total > 0) {
    return Math.min(99, Math.max(5, Math.round(translateProgress.percentage)));
  }
  return 5;
}

/**
 * The platform only retains a job's content (audio, transcript, translated
 * subtitle) for this many days. After that the DB history row may still exist
 * but the result is no longer downloadable.
 */
export const SUBTITLE_TASK_RETENTION_DAYS = 7;

/** Whether a task is past the retention window, so its result is gone. */
export function isTaskExpired(createdAt: string | null | undefined, retentionDays = SUBTITLE_TASK_RETENTION_DAYS): boolean {
  if (!createdAt) {
    return false;
  }
  const created = Date.parse(createdAt);
  if (!Number.isFinite(created)) {
    return false;
  }
  return Date.now() - created > retentionDays * 24 * 60 * 60 * 1000;
}

/** Build a task-list row from a live JobSnapshot (component + polling). */
export function taskItemFromJobSnapshot(job: JobSnapshot): SubtitleTaskItem {
  return {
    jobId: job.jobId,
    // Only the status endpoint knows the tool; leave it out otherwise so an
    // upsert doesn't wipe the value loaded from the task list.
    ...(job.tool ? { tool: job.tool } : {}),
    fileName: job.sourceFileName,
    mediaKind: job.sourceMediaKind,
    outputTitle: job.outputTitle,
    targetLanguage: job.targetLanguage,
    // The snapshot folds a cancel into "failed"; keep it apart (still a
    // terminal, non-active status everywhere).
    platformStatus: job.error?.code === "CANCELED" ? "canceled" : job.status,
    localStatus: null,
    creditCost: job.creditCost || 0,
    errorMessage: job.error.message,
    translateProgress: job.translateProgress || null,
    fetchPhase: job.fetchPhase || null,
    createdAt: job.createdAt,
    updatedAt: job.updatedAt,
  };
}

export function isSupportedMediaFile(file: File) {
  const extension = file.name.split(".").pop()?.toLowerCase() || "";
  const allowedAudioExtensions = ["mp3", "wav", "m4a", "ogg", "webm", "aac", "flac", "opus"] as const;
  const allowedVideoExtensions = ["mp4", "mov", "avi", "mkv", "webm", "m4v", "wmv"] as const;

  if (file.type.startsWith("video/") || file.type.startsWith("audio/")) {
    return true;
  }

  if (!extension) {
    return false;
  }

  return (
    allowedAudioExtensions.includes(extension as (typeof allowedAudioExtensions)[number]) ||
    allowedVideoExtensions.includes(extension as (typeof allowedVideoExtensions)[number])
  );
}
